import React from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';

const apiKey: string =
  (typeof window !== 'undefined' && (window as any).__BUGSNAG_API_KEY__) ||
  (import.meta.env.VITE_BUGSNAG_API_KEY as string | undefined) ||
  '';

if (apiKey) {
  Bugsnag.start({
    apiKey,
    plugins: [new BugsnagPluginReact()],
    appVersion:
      (typeof window !== 'undefined' && (window as any).__APP_VERSION__) ||
      (import.meta.env.VITE_APP_VERSION as string | undefined) ||
      'dev',
    releaseStage: import.meta.env.MODE,
    enabledReleaseStages: ['production'],
  });
  BugsnagPerformance.start({ apiKey });
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  FallbackComponent?: React.ComponentType<{ error: Error }>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class FallbackErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught render error:', error, info);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const { FallbackComponent } = this.props;
      if (FallbackComponent) {
        return <FallbackComponent error={this.state.error} />;
      }
      return null;
    }
    return this.props.children;
  }
}

export const BugsnagErrorBoundary: React.ComponentType<ErrorBoundaryProps> = apiKey
  ? (Bugsnag.getPlugin('react') as any).createErrorBoundary(React)
  : FallbackErrorBoundary;

export function notifyBugsnag(error: Error, metadata?: Record<string, any>): void {
  if (!apiKey) return;
  try {
    Bugsnag.notify(error, (event) => {
      if (metadata) event.addMetadata('context', metadata);
    });
  } catch {
    // Never let Bugsnag crash the app
  }
}

export { Bugsnag };
